import { useState } from "react";
import { Link } from "react-router-dom";
import { useCommitBatch, type Batch, type StagedRow } from "../../api/delivery";
import { StatusChip, type Tone } from "../../components/ui";

export type CommitResult = Record<string, number>;

const STATE_TONE: Record<string, Tone> = { draft: "draft", committed: "ok", discarded: "quiet" };

export function stateTone(state: string): Tone {
  return STATE_TONE[state] ?? "quiet";
}

/** What committing a row will do, in the office's words. */
const ACTIONS: Record<string, { label: string; tone: Tone }> = {
  create: { label: "New site", tone: "ok" },
  update: { label: "Update site", tone: "quiet" },
  skip: { label: "Skip", tone: "crit" },
  failed: { label: "Unreadable", tone: "crit" },
};

function actionOf(row: StagedRow): { label: string; tone: Tone } {
  return ACTIONS[row.action] ?? { label: row.action, tone: "quiet" };
}

/** "8 read · 3 need a look · 1 unreadable": the batch at a glance. */
function summaryText(rows: readonly StagedRow[]): string {
  const unreadable = rows.filter((r) => r.unreadable).length;
  const look = rows.filter((r) => !r.unreadable && r.issues.length > 0).length;
  const parts = [`${rows.length - unreadable} read`];
  if (look > 0) {
    parts.push(`${look} need${look === 1 ? "s" : ""} a look`);
  }
  if (unreadable > 0) {
    parts.push(`${unreadable} unreadable`);
  }
  return parts.join(" · ");
}

/** Step 3: what was read from each document, what committing it will do, and
 * what the checks found. The rows that can be committed start ticked; an
 * unreadable one cannot be. Committing writes the ticked rows only. */
export default function BatchReview({ batch, onCommitted }: { batch: Batch; onCommitted?: (result: CommitResult) => void }) {
  const commit = useCommitBatch(batch.batch.id);
  const committable = batch.rows.filter((row) => !row.unreadable && row.action !== "skip");
  const [ticked, setTicked] = useState<ReadonlySet<number>>(new Set(committable.map((r) => r.row_id)));
  const draft = batch.batch.state === "draft";

  function tick(id: number, on: boolean) {
    const next = new Set(ticked);
    if (on) {
      next.add(id);
    } else {
      next.delete(id);
    }
    setTicked(next);
  }

  return (
    <section className="card upload-review" aria-label={`Batch ${batch.batch.id}`}>
      <div className="card-head">
        <h2>Batch #{batch.batch.id}</h2>
        <StatusChip tone={stateTone(batch.batch.state)}>{batch.batch.state}</StatusChip>
        <span className="card-sub">{summaryText(batch.rows)}</span>
        <div className="card-actions">
          {batch.report_csv_url ? (
            <a className="go-link" href={batch.report_csv_url}>
              Report (CSV)
            </a>
          ) : null}
          {draft ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={commit.isPending || ticked.size === 0}
              onClick={() => commit.mutate([...ticked], { onSuccess: (made) => onCommitted?.(made.result) })}
            >
              {commit.isPending ? "Committing…" : `Commit ${ticked.size} row${ticked.size === 1 ? "" : "s"}`}
            </button>
          ) : null}
        </div>
      </div>
      {commit.isError ? (
        <p className="notice bad upload-note" role="alert">
          {commit.error.message}
        </p>
      ) : null}
      {commit.data && onCommitted === undefined ? (
        <p className="notice ok upload-note" role="status">
          {committedText(commit.data.result)}
        </p>
      ) : null}
      {batch.rows.length === 0 ? (
        <p className="empty-state">This batch has no documents.</p>
      ) : (
        <div className="upload-table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {draft ? <th className="upload-tick" /> : null}
                <th>Site read</th>
                <th>Valve</th>
                <th>Location</th>
                <th>Will</th>
                <th>Check</th>
              </tr>
            </thead>
            <tbody>
              {batch.rows.map((row) => (
                <StagedRowView key={row.row_id} row={row} draft={draft} ticked={ticked.has(row.row_id)} onTick={(on) => tick(row.row_id, on)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** "12 created, 3 updated, 1 skipped, 12 pre-issued BOQs built" -- the
 * commit's own tallies. A bill that could not be built leaves a flag on its
 * site, which is where the office builds it from. */
export function committedText(result: CommitResult): string {
  const parts = [`${result.created ?? 0} created`, `${result.updated ?? 0} updated`, `${result.skipped ?? 0} skipped`];
  if (result.held) {
    parts.push(`${result.held} held until the works order is placed`);
  }
  if (result.boqs_built) {
    parts.push(`${result.boqs_built} pre-issued BOQ${result.boqs_built === 1 ? "" : "s"} built`);
  }
  if (result.boqs_failed) {
    parts.push(`${result.boqs_failed} could not be built; see the site flags`);
  }
  return `Committed: ${parts.join(", ")}.`;
}

function StagedRowView({ row, draft, ticked, onTick }: { row: StagedRow; draft: boolean; ticked: boolean; onTick: (on: boolean) => void }) {
  const text = (key: string) => {
    const value = row.extracted[key];
    return value === null || value === undefined ? "" : String(value);
  };
  const action = actionOf(row);
  const valve = [text("valve_make"), text("valve_desc"), text("dn_scope") ? `DN${text("dn_scope")}` : ""].filter(Boolean).join(" · ");
  const rowClass = row.unreadable ? "upload-row-bad" : row.issues.length > 0 ? "upload-row-warn" : undefined;
  return (
    <tr className={rowClass}>
      {draft ? (
        <td className="upload-tick">
          <input type="checkbox" checked={ticked} disabled={row.unreadable} onChange={(e) => onTick(e.target.checked)} aria-label={`Commit ${row.filename}`} />
        </td>
      ) : null}
      <td>
        <span className="upload-site">{row.unreadable ? "Unreadable" : text("site_name") || "No site name read"}</span>
        <span className="sub mono upload-filename">
          {row.filename} ·{" "}
          <Link to={`/extraction/${row.row_id}`} className="go-link">
            review
          </Link>
        </span>
      </td>
      <td className="muted">{valve || "—"}</td>
      <td className="muted" title={row.location.label}>
        {row.location.lat !== null ? `${row.location.label}${row.location.confidence ? ` (${row.location.confidence})` : ""}` : "none"}
      </td>
      <td>
        <StatusChip tone={action.tone}>{action.label}</StatusChip>
      </td>
      <td>
        {row.issues.length === 0 ? (
          <span className="upload-ok">Looks right</span>
        ) : (
          <ul className="upload-issues">
            {row.issues.map((issue) => (
              <li key={issue.code} title={issue.code}>
                {issue.detail || issue.code}
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}
